import {
  Autocomplete,
  Badge,
  Button,
  Checkbox,
  CircularProgress,
  FormControl,
  FormControlLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import Moment from 'moment'
import {
  ClipboardOutline,
  Clock,
  Thermometer,
  Fire,
  ThermometerHigh,
  ThermometerLow,
} from 'mdi-material-ui'
import { inject } from 'mobx-react'
import { RouterStore } from 'mobx-react-router'
import React from 'react'
import { Helmet } from 'react-helmet'
import ReactPaginate from 'react-paginate'
import config from 'src/config'
import { IComponent, IUser } from 'src/Definitions'
import { searchDashboard } from 'src/services/SearchService'
import { DashboardSearchRequest } from 'src/viewModels/DashboardSearchRequest'
import { DashboardSummaryResult } from 'src/viewModels/DashboardSummaryResult'
import { pagedList } from 'src/viewModels/pagedList'
import { IStores } from '../../Stores'
import {
  CoordinationStatus,
  CoordinationStatusLabels,
} from 'src/utils/CoordinationStatus'
import EOCStatus from 'src/utils/EOCStatus'
import stores from '../../Stores'

interface ISurgicalPatientFlowModuleProps {
  rerouteToPath?: (path: string) => void
  routerStore?: RouterStore
  getAllComponents?: () => Promise<void>
  components?: IComponent[]
  getAllUsers?: (size: number, role: string) => void
  cares?: IUser[]
  setReturn?: (path?: string) => Promise<void>
  setDashboardRequest?: (request: DashboardSearchRequest) => void
  storedRequest?: DashboardSearchRequest
}

const getDefaultCoordinatorId = (): string => {
  if (
    stores.global.user &&
    stores.global.user.roles &&
    (stores.global.user.roles.includes(config.ObxRoles.CareCoordinator) ||
      stores.global.user.roles.includes(config.ObxRoles.CareCoordinatorManager))
  ) {
    return stores.global.user.id
  }

  return ''
}

class SurgicalPatientFlowModule extends React.Component<ISurgicalPatientFlowModuleProps> {
  public state = {
    searchApptDateStart: this.props.storedRequest?.apptDateStart ?? undefined,
    searchApptDateEnd: this.props.storedRequest?.apptDateEnd ?? undefined,
    searchProviderGroupName: this.props.storedRequest?.providerGroupName ?? '',
    searchCoordinatorId:
      this.props.storedRequest?.coordinatorId ?? getDefaultCoordinatorId(),
    searchComponent: this.props.storedRequest?.componentId ?? '',
    searchPatientFirstName: this.props.storedRequest?.patientFirstName ?? '',
    searchPatientLastName: this.props.storedRequest?.patientLastName ?? '',
    searchEocLegacy: this.props.storedRequest?.eocLegacy ?? '',
    searchInvoiceSurgeryStatus: this.props.storedRequest?.invoiceSurgeryStatus ?? '',
    searchCoordinationStatus: this.props.storedRequest?.coordinationStatus as
      | CoordinationStatus
      | undefined,
    searchPriority: this.props.storedRequest?.priority as number | undefined,
    searchIsOverdue: this.props.storedRequest?.isOverdue ?? false,
    gridData: new pagedList().items as DashboardSummaryResult[],
    page: 1,
    pageCount: 0,
    firstRecordIndex: 0,
    lastRecordIndex: 0,
    totalItems: 0,
    pageSize: 20,
    loadingGrid: true,
  }

  private eocStatusOptions = [
    EOCStatus.Cancelled,
    EOCStatus.Evaluation,
    EOCStatus.CaseClosed,
    EOCStatus.InjectionBundle,
    EOCStatus.NonSurgical,
    EOCStatus.NonSurgicalBundle,
    EOCStatus.PotentialNonSurgical,
    EOCStatus.SurgicalBundle,
    EOCStatus.SxPostponed,
  ]

  public componentDidMount() {
    this.props.getAllComponents!()
    this.props.getAllUsers!(5000, config.ObxRoles.CareCoordinator)
    this.getDashboardData()
  }

  private DisplayPriority: {
    [key: string]: { label: string; icon: typeof Thermometer; color: string }
  } = {
    0: { label: 'Low', icon: ThermometerLow, color: '#757575' },
    1: { label: 'Medium', icon: Thermometer, color: '#f9a825' },
    2: { label: 'High', icon: ThermometerHigh, color: '#d32f2f' },
  }

  private splitTasks = (tasks: string | undefined) =>
    (tasks ?? '')
      .split('|')
      .map((task) => task.trim())
      .filter((task) => task)

  private getOverdueTasks = (record: DashboardSummaryResult) => {
    const high = this.splitTasks(record.highTasks)
    const medium = this.splitTasks(record.mediumTasks)
    const low = this.splitTasks(record.lowTasks)

    return this.splitTasks(record.overdueTasks).map((name) => ({
      name,
      level: high.includes(name)
        ? 2
        : medium.includes(name)
          ? 1
          : low.includes(name)
            ? 0
            : undefined,
    }))
  }

  private hasOverdueAtLevel = (record: DashboardSummaryResult, level: number) =>
    this.getOverdueTasks(record).some((task) => task.level === level)

  private renderPriorityIcon = (record: DashboardSummaryResult) => {
    const priority = this.DisplayPriority[record.priority]
    if (!priority) {
      return <></>
    }
    const overdueTasks = this.getOverdueTasks(record)
    const isOverdue =
      record.priority === 2 ? this.hasOverdueAtLevel(record, 2) : overdueTasks.length > 0
    const topOverdueLevel = [2, 1, 0].find((level) =>
      overdueTasks.some((task) => task.level === level)
    )
    const PriorityIcon = isOverdue ? Fire : priority.icon
    const iconColor = isOverdue
      ? this.DisplayPriority[topOverdueLevel ?? record.priority].color
      : priority.color

    return (
      <Tooltip
        title={
          <>
            <div style={{ fontWeight: 600 }}>
              {overdueTasks.length > 0 ? 'Overdue Items' : `${priority.label} Priority`}
            </div>
            {[2, 1, 0, undefined].map((level) => {
              const tasks = overdueTasks.filter((task) => task.level === level)
              if (tasks.length === 0) {
                return null
              }
              return (
                <div key={level ?? 'other'} style={{ marginTop: '2px' }}>
                  <div style={{ fontStyle: 'italic' }}>
                    {level !== undefined ? this.DisplayPriority[level].label : 'Other'}
                  </div>
                  {tasks.map((task) => (
                    <div key={task.name} style={{ paddingLeft: '8px' }}>
                      • {task.name}
                    </div>
                  ))}
                </div>
              )
            })}
          </>
        }
      >
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <PriorityIcon
            htmlColor={iconColor}
            sx={{
              transition: 'transform 0.2s ease-out',
              '&:hover': { transform: 'translateY(-4px) scale(1.15)' },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
            }}
          />
        </div>
      </Tooltip>
    )
  }

  private renderTaskCounts = (record: DashboardSummaryResult) => {
    const counts = [
      { priority: this.DisplayPriority[2], count: record.highTasks },
      { priority: this.DisplayPriority[1], count: record.mediumTasks },
      { priority: this.DisplayPriority[0], count: record.lowTasks },
    ]

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        {counts
          .filter((c) => c.count)
          .map((c) => {
            const PriorityIcon = c.priority.icon
            return (
              <span
                key={c.priority.label}
                style={{ display: 'inline-flex', alignItems: 'flex-start' }}
              >
                <PriorityIcon htmlColor={c.priority.color} style={{ fontSize: '18px' }} />
                {c.count}
              </span>
            )
          })}
      </div>
    )
  }

  private getDashboardData = async (setSavedSearch: boolean = false) => {
    const { page, pageSize, searchPatientFirstName, searchPatientLastName } = this.state

    const data: DashboardSearchRequest = {
      patientFirstName: searchPatientFirstName,
      patientLastName: searchPatientLastName,
      apptDateStart: this.state.searchApptDateStart,
      apptDateEnd: this.state.searchApptDateEnd,
      providerGroupName: this.state.searchProviderGroupName,
      coordinatorId: this.state.searchCoordinatorId,
      componentId: this.state.searchComponent,
      eocLegacy: this.state.searchEocLegacy,
      invoiceSurgeryStatus: this.state.searchInvoiceSurgeryStatus,
      coordinationStatus: this.state.searchCoordinationStatus,
      priority: this.state.searchPriority,
      isOverdue: this.state.searchIsOverdue || undefined,
      page: page,
      pageSize: pageSize,
    }

    try {
      const response = await searchDashboard(data)
      const totalItems = response.totalItems || 0
      const firstRecordIndex = totalItems > 0 ? (page - 1) * pageSize + 1 : 0
      const lastRecordIndex =
        totalItems > 0 ? (page * pageSize < totalItems ? page * pageSize : totalItems) : 0

      this.setState({
        gridData: response.items || [],
        pageCount: response.totalPages || 0,
        totalItems: totalItems,
        firstRecordIndex: firstRecordIndex,
        lastRecordIndex: lastRecordIndex,
        loadingGrid: false,
      })
      if (setSavedSearch) {
        this.props.setDashboardRequest!(data)
      }
    } catch (_) {
      this.setState({
        gridData: [],
        pageCount: 0,
        totalItems: 0,
        loadingGrid: false,
      })
    }
  }

  private handlePageChange = (event: any) => {
    const page = event.selected
    this.setState({ page: page + 1, loadingGrid: true }, () => this.getDashboardData())
  }

  private isSearchGridDataEmpty = () => {
    return this.state.totalItems == 0
  }

  private paginationInfo = () => {
    if (!this.isSearchGridDataEmpty()) {
      return (
        <div className="pagination">
          <Typography variant="caption" gutterBottom>
            Showing {this.state.firstRecordIndex} to {this.state.lastRecordIndex} of{' '}
            {this.state.totalItems} entries
          </Typography>
        </div>
      )
    } else {
      return <></>
    }
  }

  private search = (event: any) => {
    this.setState({ page: 1, loadingGrid: true }, () => this.getDashboardData(true))
    event.preventDefault()
  }

  private clearSearch = () => {
    var select_box = document.getElementById('componentSelect') as HTMLSelectElement
    if (select_box != undefined) {
      select_box.selectedIndex = 0
    }

    var care_select_box = document.getElementById('careSelect') as HTMLSelectElement
    if (care_select_box != undefined) {
      care_select_box.selectedIndex = 0
    }
    var start = document.getElementById('startDate') as HTMLInputElement
    if (start != undefined) {
      start.value = ''
    }
    var end = document.getElementById('endDate') as HTMLInputElement
    if (end != undefined) {
      end.value = ''
    }
    this.props.setDashboardRequest!(new DashboardSearchRequest())
    this.setState({
      searchApptDateStart: undefined,
      searchApptDateEnd: undefined,
      searchProviderGroupName: '',
      searchCoordinatorId: getDefaultCoordinatorId(),
      searchComponent: '',
      searchPatientFirstName: '',
      searchPatientLastName: '',
      searchEocLegacy: '',
      searchInvoiceSurgeryStatus: '',
      searchCoordinationStatus: undefined,
      searchPriority: undefined,
      searchIsOverdue: false,
      gridData: [],
      pageCount: 0,
      totalItems: 0,
    })
  }

  private handlePatientFirstNameChange = (event: any) => {
    this.setState({ searchPatientFirstName: event.target.value })
  }

  private handlePatientLastNameChange = (event: any) => {
    this.setState({ searchPatientLastName: event.target.value })
  }

  private handleEocLegacyChange = (event: any) => {
    this.setState({ searchEocLegacy: event.target.value })
  }

  private handleProviderGroupNameChange = (event: any) => {
    this.setState({ searchProviderGroupName: event.target.value })
  }

  private handleStartDateChange = (date: any) => {
    this.setState({ searchApptDateStart: date ? Moment(date).format('YYYY-MM-DD') : '' })
  }

  private handleEndDateChange = (date: any) => {
    this.setState({ searchApptDateEnd: date ? Moment(date).format('YYYY-MM-DD') : '' })
  }

  private redirectToComponents = (
    patientId?: string,
    referralId?: string,
    episodeOfCareId?: string,
    scheduleId?: string
  ) => {
    if (patientId && referralId && episodeOfCareId && scheduleId) {
      this.props.setReturn!('/').then(() => {
        sessionStorage.setItem('patientTab', '0')
        sessionStorage.setItem('eocTab', '0')
        this.props.routerStore?.push(
          `/patients/patient/${patientId}/referral/${referralId}/episodeOfCare/${episodeOfCareId}/schedule/${scheduleId}`
        )
      })
    }
  }

  private renderEocIcon = (record: DashboardSummaryResult) => {
    const coordinationStatus = record.coordinationStatus?.trim()
    const status = coordinationStatus
      ? `${record.invoiceSurgeryStatus}-${coordinationStatus}`
      : record.invoiceSurgeryStatus

    const badgeLabel = coordinationStatus?.replace(/\s*only$/i, '')

    return (
      <Tooltip title={`Click here to open ${record.eocLegacy} (${status})`}>
        <span style={{ display: 'flex', marginLeft: '8px', marginRight: '12px' }}>
          <Badge
            badgeContent={badgeLabel}
            invisible={!badgeLabel}
            color="secondary"
            anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
            sx={{
              '& .MuiBadge-badge': {
                fontSize: '9px',
                height: '14px',
                minWidth: '14px',
                padding: '0 3px',
              },
            }}
          >
            <Clock
              style={{ cursor: 'pointer' }}
              color="primary"
              onClick={() => {
                this.redirectToEoc(record.patientId, record.referralId, record.eocId)
              }}
            />
          </Badge>
        </span>
      </Tooltip>
    )
  }

  private redirectToEoc = (
    patientId?: string,
    referralId?: string,
    episodeOfCareId?: string
  ) => {
    if (patientId && referralId && episodeOfCareId) {
      this.props.setReturn!('/').then(() => {
        sessionStorage.setItem('patientTab', '0')
        sessionStorage.setItem('eocTab', '0')
        this.props.routerStore?.push(
          `/patients/patient/${patientId}/referral/${referralId}/episodeOfCare/${episodeOfCareId}`
        )
      })
    }
  }

  private mapSearchGridData = () => {
    if (this.isSearchGridDataEmpty()) {
      return (
        <TableRow className="gridNoPadding" key={1}>
          <TableCell colSpan={8} align="center">
            No records found
          </TableCell>
        </TableRow>
      )
    } else {
      return this.state.gridData.map((record, index) => (
        <TableRow
          className="gridPaddingDense"
          key={index}
          sx={{ '& > td': { verticalAlign: 'top' } }}
        >
          <TableCell style={{ paddingLeft: '30px', width: '1%' }}>
            {this.renderPriorityIcon(record)}
          </TableCell>
          <TableCell>
            <div>{record.patientName}</div>
            {record.patientDob && (
              <div style={{ fontSize: '12px', color: '#757575' }}>
                DOB: {Moment.utc(record.patientDob).format('MM/DD/YYYY')}
              </div>
            )}
          </TableCell>
          <TableCell>
            {record.apptDate ? Moment.utc(record.apptDate).format('MM/DD/YYYY') : ''}
          </TableCell>
          <TableCell>{record.component}</TableCell>
          <TableCell>{this.renderTaskCounts(record)}</TableCell>
          <TableCell>{record.providerGroupName}</TableCell>
          <TableCell>{record.careCoordinator}</TableCell>
          <TableCell style={{ whiteSpace: 'nowrap' }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Tooltip
                title={
                  record.component
                    ? `Click here to open ${record.component} tasks`
                    : 'Tasks'
                }
              >
                <span style={{ display: 'flex' }}>
                  <ClipboardOutline
                    style={{ cursor: 'pointer' }}
                    color="primary"
                    onClick={() => {
                      this.redirectToComponents(
                        record.patientId,
                        record.referralId,
                        record.eocId,
                        record.scheduleId
                      )
                    }}
                  />
                </span>
              </Tooltip>
              {this.renderEocIcon(record)}
            </div>
          </TableCell>
        </TableRow>
      ))
    }
  }

  public render() {
    return (
      <>
        <Helmet>
          <title>Coordinator Dashboard</title>
        </Helmet>
        <Grid container style={{ width: '100%', paddingBottom: '20px' }}>
          {this.state.loadingGrid && (
            <div
              style={{
                position: 'fixed',
                display: 'flex',
                width: '100%',
                height: '100%',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                cursor: 'pointer',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 800,
              }}
            >
              <CircularProgress size={100} />
            </div>
          )}
          <form onSubmit={this.search}>
            <Grid container item style={{ margin: '10px', marginLeft: '30px' }} xs={11}>
              <Typography
                color="primary"
                component="h6"
                variant="h6"
                gutterBottom
                style={{ paddingBottom: '10px' }}
              >
                SEARCH CRITERIA
              </Typography>
              <Grid container direction="row" spacing={2}>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <TextField
                      inputProps={{ style: { height: '24px' } }}
                      value={this.state.searchPatientFirstName}
                      onChange={this.handlePatientFirstNameChange}
                      label="Patient First Name"
                      name="patientFirst"
                      InputLabelProps={{ shrink: true }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <TextField
                      inputProps={{ style: { height: '24px' } }}
                      label="Patient Last Name"
                      name="patientLast"
                      variant="outlined"
                      value={this.state.searchPatientLastName}
                      onChange={this.handlePatientLastNameChange}
                      InputLabelProps={{ shrink: true }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchEocLegacy}
                      onChange={this.handleEocLegacyChange}
                      label="EOC/Legacy"
                      name="eocLegacy"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { height: '24px' } }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item container xs={4} spacing={1}>
                  <Grid item xs={5}>
                    <DatePicker
                      value={
                        this.state.searchApptDateStart
                          ? Moment(this.state.searchApptDateStart)
                          : null
                      }
                      onChange={this.handleStartDateChange}
                      label="Start Date"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          InputLabelProps: { shrink: true },
                        },
                        openPickerIcon: { fontSize: 'small' },
                      }}
                    />
                  </Grid>
                  <Grid
                    item
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <strong> - </strong>
                  </Grid>
                  <Grid item xs={5}>
                    <DatePicker
                      value={
                        this.state.searchApptDateEnd
                          ? Moment(this.state.searchApptDateEnd)
                          : null
                      }
                      onChange={this.handleEndDateChange}
                      label="End Date"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          InputLabelProps: { shrink: true },
                        },
                        openPickerIcon: { fontSize: 'small' },
                      }}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={3}>
                  <FormControl fullWidth>
                    <TextField
                      value={this.state.searchProviderGroupName}
                      onChange={this.handleProviderGroupNameChange}
                      label="Provider Group Name"
                      name="providerName"
                      InputLabelProps={{ shrink: true }}
                      inputProps={{ style: { height: '24px' } }}
                      variant="outlined"
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={3}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <Autocomplete
                      size="small"
                      id="componentSelect"
                      options={this.props.components || []}
                      getOptionLabel={(option: IComponent) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      value={
                        this.props.components?.find(
                          (c) => String(c.id) === String(this.state.searchComponent)
                        ) || null
                      }
                      onChange={(_: any, value: IComponent | null) =>
                        this.setState({ searchComponent: value ? value.id : '' })
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Component"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <Autocomplete
                      size="small"
                      id="careCoordinatorSelect"
                      options={this.props.cares || []}
                      getOptionLabel={(option: IUser) => option.name}
                      isOptionEqualToValue={(option, value) =>
                        String(option.id) === String(value.id)
                      }
                      value={
                        this.props.cares?.find(
                          (c) => String(c.id) === String(this.state.searchCoordinatorId)
                        ) || null
                      }
                      onChange={(_: any, value: IUser | null) =>
                        this.setState({ searchCoordinatorId: value ? value.id : '' })
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Care Coordinator"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={2}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <Autocomplete
                      size="small"
                      id="eocStatusSelect"
                      options={this.eocStatusOptions}
                      value={this.state.searchInvoiceSurgeryStatus || null}
                      onChange={(_: any, value: string | null) =>
                        this.setState({ searchInvoiceSurgeryStatus: value ?? '' })
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="EOC Status"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1.5}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <Autocomplete
                      size="small"
                      id="coordinationStatusSelect"
                      options={[CoordinationStatus.DMEOnly, CoordinationStatus.PTOnly]}
                      getOptionLabel={(option: CoordinationStatus) =>
                        CoordinationStatusLabels[option]
                      }
                      value={this.state.searchCoordinationStatus ?? null}
                      onChange={(_: any, value: CoordinationStatus | null) =>
                        this.setState({ searchCoordinationStatus: value ?? undefined })
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Coordination Status"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={1.5}>
                  <FormControl style={{ width: '100%' }} variant="outlined">
                    <Autocomplete
                      size="small"
                      id="prioritySelect"
                      options={[2, 1, 0]}
                      getOptionLabel={(option: number) =>
                        this.DisplayPriority[option].label
                      }
                      renderOption={(props: any, option: number) => {
                        const OptionIcon = this.DisplayPriority[option].icon
                        return (
                          <li {...props} key={option}>
                            <OptionIcon
                              htmlColor={this.DisplayPriority[option].color}
                              style={{ fontSize: '18px', marginRight: '6px' }}
                            />
                            {this.DisplayPriority[option].label}
                          </li>
                        )
                      }}
                      value={this.state.searchPriority ?? null}
                      onChange={(_: any, value: number | null) =>
                        this.setState({ searchPriority: value ?? undefined })
                      }
                      renderInput={(params: any) => (
                        <TextField
                          {...params}
                          label="Priority"
                          variant="outlined"
                          InputLabelProps={{ shrink: true }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item style={{ display: 'flex', alignItems: 'center' }}>
                  <FormControlLabel
                    control={
                      <Checkbox
                        size="small"
                        checked={this.state.searchIsOverdue}
                        onChange={(event) =>
                          this.setState({ searchIsOverdue: event.target.checked })
                        }
                      />
                    }
                    label="Overdue only"
                  />
                </Grid>
                <Grid
                  container
                  direction="row"
                  spacing={2}
                  sx={{ ml: 1, pb: 1.25, pt: 1.25 }}
                >
                  <Grid item style={{ paddingLeft: '8px' }}>
                    <Button
                      style={{ marginLeft: '1px' }}
                      type="submit"
                      color="primary"
                      size="medium"
                      variant="contained"
                    >
                      Search
                    </Button>
                  </Grid>
                  <Grid item>
                    <Button
                      color="secondary"
                      onClick={this.clearSearch}
                      size="medium"
                      variant="contained"
                    >
                      Clear Search
                    </Button>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
          </form>
          <Grid item xs={12}>
            <Table aria-label="simple table" size="small" style={{ width: '100%' }}>
              <TableHead>
                <TableRow className="gridPaddingDense">
                  <TableCell style={{ paddingLeft: '30px', width: '1%' }}>
                    Priority
                  </TableCell>
                  <TableCell>Patient</TableCell>
                  <TableCell>Appt</TableCell>
                  <TableCell>Component</TableCell>
                  <TableCell>Tasks</TableCell>
                  <TableCell>Provider Group</TableCell>
                  <TableCell>Coordinator</TableCell>
                  <TableCell style={{ width: '1%' }}></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>{this.mapSearchGridData()}</TableBody>
            </Table>
            <div className="pagination-row">
              {this.paginationInfo()}
              {this.state.pageCount > 0 && (
                <ReactPaginate
                  previousLabel={'<'}
                  nextLabel={'>'}
                  onPageChange={this.handlePageChange}
                  forcePage={this.state.page - 1}
                  pageCount={this.state.pageCount}
                  containerClassName={'pagination'}
                  activeClassName={'active'}
                  //@ts-ignore
                  renderOnZeroPageCount={null}
                />
              )}
            </div>
          </Grid>
        </Grid>
      </>
    )
  }
}

const InjectedSurgicalPatientFlowModule = inject<
  IStores,
  ISurgicalPatientFlowModuleProps,
  Partial<ISurgicalPatientFlowModuleProps>,
  any
>((stores: IStores) => ({
  rerouteToPath: stores.global.rerouteToPath,
  routerStore: stores.scheduleAppointmentTasks.routerStore,
  getAllComponents: stores.components.getAllComponents,
  components: stores.components.components,
  getAllUsers: stores.users.getAllUsers,
  cares: stores.users.users,
  setReturn: stores.global.setReturn,
  setDashboardRequest: stores.global.setDashboardRequest,
  storedRequest: stores.global.dashboardRequest,
}))(SurgicalPatientFlowModule)

export default InjectedSurgicalPatientFlowModule
